/**
 * First-touch attribution capture for the app.
 *
 * The marketing site (mediloop.ai) captures utm_* into cookies, but those
 * cookies don't follow over to app.mediloop.ai unless they're scoped to
 * .mediloop.ai (which the middleware does in production). For
 * belt-and-suspenders — Vercel previews, ITP-restricted browsers, ad
 * blockers — we also forward attribution params on every CTA link
 * (see apps/web/src/lib/appLink.js).
 *
 * This module is the receiving end on the app:
 *   1. captureAttributionFromUrl() pulls utm_* + ph_distinct_id + ml_vid
 *      out of the current URL on signup-page load and stashes them in
 *      localStorage. Strictly first-touch — never overwritten.
 *   2. getStoredAttribution() reads the blob back during onboarding so
 *      we can write it to profiles.utm_* alongside the upsert.
 *
 * Storage key: 'mediloop_attribution' (JSON blob).
 */

import { supabase } from './supabase';

const STORAGE_KEY = 'mediloop_attribution';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  initial_referrer?: string;
  landing_page?: string;
  ml_vid?: string; // visitor id from marketing-site middleware
  acquisition_channel?: string; // derived: search / ai_assistant / social / referral / direct / <paid utm_source>
  region_state?: string; // 2-letter US state from first-touch GeoIP (Vercel edge)
  captured_at?: string; // ISO timestamp
};

/**
 * Read the marketing site's `mediloop_utm` cookie. The web middleware
 * (apps/web/middleware.js) writes captured UTMs there scoped to
 * `.mediloop.ai`, so the cookie survives the hop to app.mediloop.ai even
 * when the params never make it onto the app URL — which is exactly what
 * happens on the Meta funnel, where the CTA goes straight to Stripe
 * Checkout and the success redirect carries no UTMs. JS-readable
 * (httpOnly:false). Returns {} if absent or unparseable.
 */
function readUtmCookie(): Partial<Record<(typeof UTM_KEYS)[number], string>> {
  if (typeof document === 'undefined') return {};
  try {
    const match = document.cookie.match(/(?:^|; )mediloop_utm=([^;]+)/);
    if (!match) return {};
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Read any attribution params off the current URL and persist them.
 * Idempotent and first-touch: if attribution is already stored, this is
 * a no-op. Call on Signup.tsx mount so a user who lands on app.mediloop.ai
 * with utm_source=fb gets that bound to their profile at onboarding.
 */
/**
 * Referrers that are NOT a real acquisition source: our own site plus the
 * payment/auth intermediaries a buyer always bounces through. Post-checkout
 * the app's document.referrer is checkout.stripe.com; treating that as the
 * source is what stamped every buyer's initial_referrer as Stripe. Anything
 * matching here is ignored in favour of the marketing first-touch cookie.
 */
function isIntermediaryReferrer(hostname: string): boolean {
  return /(^|\.)(mediloop\.ai|stripe\.com)$/.test(hostname);
}

/**
 * Read the marketing site's first-touch cookie (mediloop_ft), written by
 * apps/web/middleware.js scoped to .mediloop.ai. It carries the TRUE external
 * referrer + landing path from the first mediloop.ai pageview — the only
 * source signal for organic / AI-assistant / direct traffic, which has no
 * utm_* to ride the existing mediloop_utm cookie. Returns null if absent.
 */
function readFirstTouchCookie(): { r?: string; l?: string; s?: string; c?: string } | null {
  if (typeof document === 'undefined') return null;
  try {
    const match = document.cookie.match(/(?:^|; )mediloop_ft=([^;]+)/);
    if (!match) return null;
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Coarse acquisition channel from the first-touch referrer + any UTMs.
 * Best-effort and reporting-oriented; the self-reported onboarding
 * `referral_source` is the stronger signal and is stored separately. One hard
 * limit: Google's Gemini passes referrer as google.com (a Google property),
 * so it is indistinguishable from organic search by referrer alone — only the
 * self-report catches Gemini. ChatGPT / Perplexity / Claude / Copilot do send
 * distinct referrers and are detected here.
 */
export function classifyChannel(
  referrer?: string | null,
  utmSource?: string | null,
  utmMedium?: string | null,
): string {
  const src = (utmSource || '').toLowerCase();
  const medium = (utmMedium || '').toLowerCase();
  // Auto-derived organic channels (seeded from the referrer by apps/web
  // middleware when there is no paid UTM/click-ID) carry a marker medium.
  // Map them back to the normalized bucket so acquisition_channel stays a
  // clean taxonomy and organic google (medium=organic) never collides with
  // PAID google (utm_source 'google' + medium 'cpc'). Any other set source
  // (real/paid UTMs, manual tags like medium=email) returns verbatim as before.
  const AUTO_ORGANIC: Record<string, string> = {
    organic: 'search',
    social: 'social',
    ai_assistant: 'ai_assistant',
    referral: 'referral',
    none: 'direct',
  };
  if (src) {
    if (medium && Object.prototype.hasOwnProperty.call(AUTO_ORGANIC, medium)) {
      return AUTO_ORGANIC[medium];
    }
    return src;
  }
  if (!referrer) return 'direct';
  let host = '';
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return 'direct';
  }
  if (/(^|\.)(chatgpt\.com|openai\.com|perplexity\.ai|claude\.ai|copilot\.microsoft\.com)$/.test(host)) {
    return 'ai_assistant';
  }
  if (/(^|\.)(google\.[a-z.]+|bing\.com|duckduckgo\.com|search\.yahoo\.com|ecosia\.org|brave\.com)$/.test(host)) {
    return 'search';
  }
  if (/(^|\.)(facebook\.com|instagram\.com|t\.co|twitter\.com|x\.com|reddit\.com|linkedin\.com|youtube\.com|tiktok\.com|pinterest\.com)$/.test(host)) {
    return 'social';
  }
  return 'referral';
}

export function captureAttributionFromUrl(): void {
  if (typeof window === 'undefined') return;

  // Already captured? First-touch is sticky — bail out.
  if (localStorage.getItem(STORAGE_KEY)) return;

  const params = new URLSearchParams(window.location.search);
  const attribution: Attribution = {};

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) attribution[key] = value.slice(0, 200);
  }

  // Fallback to the `.mediloop.ai`-scoped `mediloop_utm` cookie when the URL
  // carried no UTMs. The Meta landing page sends users straight to Stripe
  // Checkout, whose success redirect lands on the app with no utm_* params —
  // so without this every paid signup attributed to nothing (utm_source null
  // on the profile). The cookie is set on the marketing site by middleware
  // and survives the subdomain hop.
  if (!UTM_KEYS.some((k) => attribution[k])) {
    const cookieUtm = readUtmCookie();
    for (const key of UTM_KEYS) {
      const value = cookieUtm[key];
      if (value) attribution[key] = String(value).slice(0, 200);
    }
  }

  const vid = params.get('ml_vid');
  if (vid) attribution.ml_vid = vid.slice(0, 100);

  // First-touch referrer + landing page. Prefer the marketing-site cookie
  // (mediloop_ft): it holds the TRUE external referrer and landing path from
  // the first mediloop.ai pageview, the only source signal for organic /
  // AI-assistant / direct traffic. Fall back to document.referrer only for
  // visitors who landed straight on the app (no marketing hop) — and even then
  // ignore our own site and the Stripe/auth intermediaries a buyer bounces
  // through, which is what used to stamp every purchase as checkout.stripe.com.
  const firstTouch = readFirstTouchCookie();
  if (firstTouch?.r) {
    attribution.initial_referrer = firstTouch.r.slice(0, 500);
  } else if (!firstTouch) {
    const ref = document.referrer || '';
    if (ref) {
      try {
        const refHost = new URL(ref, window.location.href).hostname || '';
        if (!isIntermediaryReferrer(refHost)) {
          attribution.initial_referrer = ref.slice(0, 500);
        }
      } catch {
        /* malformed referrer — ignore */
      }
    }
  }

  // Landing page: prefer the marketing first-touch landing path (where they
  // actually entered the funnel); otherwise the current app URL.
  attribution.landing_page = firstTouch?.l
    ? `https://mediloop.ai${firstTouch.l}`.slice(0, 1000)
    : window.location.href.slice(0, 1000);

  // Derive a coarse acquisition channel for reporting. Complements the
  // self-reported onboarding referral_source (which alone catches Gemini).
  attribution.acquisition_channel = classifyChannel(
    attribution.initial_referrer,
    attribution.utm_source,
    attribution.utm_medium,
  );

  // First-touch US state from the marketing middleware's GeoIP capture —
  // backfills profiles.state (onboarding never asks for it).
  if (firstTouch?.s) {
    attribution.region_state = firstTouch.s.slice(0, 8);
  }

  // Only persist if we actually got something attribution-shaped.
  if (
    Object.keys(attribution).some((k) => k !== 'landing_page' && attribution[k as keyof Attribution])
  ) {
    attribution.captured_at = new Date().toISOString();
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    } catch {
      /* localStorage disabled (Safari private, etc.) — silent no-op */
    }
  }
}

/**
 * Read the stored attribution blob. Returns null if nothing captured.
 * Used at the end of onboarding when writing to profiles.utm_*.
 */
export function getStoredAttribution(): Attribution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Clear the attribution blob. Currently unused — first-touch is supposed
 * to be sticky for the life of the localStorage. Exposed for tests.
 */
export function clearStoredAttribution(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Write the stored first-touch attribution onto the user's profile row.
 * Call this as soon as the account exists (right after email verification)
 * so attribution is captured even if the user never finishes onboarding.
 * Previously utm_* was only written at the end of onboarding, so anyone who
 * dropped off mid-onboarding was recorded with no source — a real blind spot.
 *
 * First-touch & idempotent: if the profile already has a utm_source this is a
 * no-op, so we never overwrite the campaign that originally acquired the user.
 * Safe to call from multiple places (verify + onboarding). Never throws —
 * attribution must never block the auth flow or navigation.
 */
export async function persistAttributionToProfile(userId: string): Promise<void> {
  if (!userId) return;
  const attribution = getStoredAttribution();
  if (!attribution) return;

  // Backfill state from the first-touch GeoIP region when the profile has none.
  // Runs independent of the source gate below — location is worth capturing even
  // for direct/unknown-source signups. `.is('state', null)` keeps it first-touch,
  // so a value set elsewhere is never clobbered.
  if (attribution.region_state) {
    try {
      await supabase
        .from('profiles')
        .update({ state: attribution.region_state })
        .eq('id', userId)
        .is('state', null);
    } catch {
      /* best-effort — never block the auth flow */
    }
  }

  // Nothing source-shaped to write? Bail (landing_page alone isn't attribution).
  const hasSource =
    attribution.utm_source ||
    attribution.utm_medium ||
    attribution.utm_campaign ||
    attribution.utm_content ||
    attribution.utm_term ||
    attribution.initial_referrer;
  if (!hasSource) return;

  try {
    // First-touch: don't clobber an already-attributed profile.
    const { data: existing } = await supabase
      .from('profiles')
      .select('utm_source')
      .eq('id', userId)
      .maybeSingle();
    if (existing?.utm_source) return;

    // The profile row already exists (created by the handle_new_user trigger
    // at signup), so update() — not upsert — avoids any insert-path NOT NULL
    // issues. A 0-row update (rare race before the trigger fires) is harmless;
    // the onboarding write remains the backstop.
    await supabase
      .from('profiles')
      .update({
        utm_source: attribution.utm_source ?? null,
        utm_medium: attribution.utm_medium ?? null,
        utm_campaign: attribution.utm_campaign ?? null,
        utm_content: attribution.utm_content ?? null,
        utm_term: attribution.utm_term ?? null,
        initial_referrer: attribution.initial_referrer ?? null,
        landing_page: attribution.landing_page ?? null,
      })
      .eq('id', userId);

    // acquisition_channel lives behind migration 059; write it in a separate
    // best-effort update so a pre-migration deploy can't fail the utm_*/
    // referrer write above (the whole block is try/caught either way).
    if (attribution.acquisition_channel) {
      await supabase
        .from('profiles')
        .update({ acquisition_channel: attribution.acquisition_channel })
        .eq('id', userId);
    }
  } catch {
    /* attribution write must never break the auth flow — swallow */
  }
}
